/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {isNil, some} from 'lodash';
import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
import {verifyCrossEnvironmentAccess} from '@ion/applications/src/env/env';
import {initializeAnalyticsStore} from '@ion/applications/src/pages/analytics/store';
import {getScoresParams} from '@ion/applications/src/pages/reports/reportsUtils';
import {setQueryParams} from '@ion/applications/src/query/state';
import {isDevMode} from '@ion/applications/src/utils/env';
import Datadog from '@ion/core/src/analytics/Datadog';
import Dynatrace from '@ion/core/src/analytics/Dynatrace';
import Pendo from '@ion/core/src/analytics/Pendo';
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage
} from '@ion/core/src/capabilities/storage';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {get} from '@ion/core/src/environment/environment';
import {error, info} from '@ion/core/src/logger/logger';
import {
  PreferencesStore
} from '@ion/core/src/preferences/types';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {getRole} from '@ion/core/src/security/helpers';
import {
  getAlertsFired
} from '@ion/core/src/services/Alerts';
import {getCVSAssets} from '@ion/core/src/services/ArcGIS';
import {CVSQueryArgs} from '@ion/core/src/services/ArcGISUtils';
import {
  getAccount,
  getAsset,
  getDevice,
  getSubAccounts,
  logout
} from '@ion/core/src/services/Connect';
import {
  entityId,
  parseExtendedAttributes
} from '@ion/core/src/services/ConnectUtils';
import Device from '@ion/core/src/services/Device';
import GroupProperties, {getWebMapId} from '@ion/core/src/services/GroupProperties';
import {setReadOnlyMode} from '@ion/core/src/services/ReadOnlyInterceptor';
import Sisense from '@ion/core/src/services/Sisense';
import {constructDriverScores} from '@ion/core/src/services/SisenseUtils';
import {setUserTimeZone} from '@ion/core/src/timezone/timezone';
import {Account, Role, User} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {endOfDay, startOfDay} from '@ion/core/src/utils/DateUtil';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {DateSelectorValues, SideSheetView, TrackingMode} from '@ion/dls/src/types/types';
import {
  currentUserQuery,
  initializationQuery
} from '@ion/services/src/graphql/QueryAdapter';
import APM from '@ion/telemetry/src/apm/APM';
import getInitializationPerformanceMonitor from '@ion/telemetry/src/apm/monitors/InitializationPerformanceMonitor';
import changePage, {
  getFeatureByRoute,
  getRoute,
  getRouteDefinition,
  Route
} from '@/routes/RouteDefinitions';
import {applyHistoryTimeMode} from '@/routes/tracking/hooks/history/useApplyHistoryTimeMode';
import FeatureFlags from '@/security/FeatureFlags';
import Features, {ADMIN_COMMAND_CENTER, hasAppNotificationAccess} from '@/security/Features';
import {verifyThemeAccess} from '@/security/themeAccess';
import {addErrorInterceptor} from '@/store/actions/errors';
import {
  initializePageSecurity,
  setDeepLinkRestriction,
  setPage,
  setSideSheetStatus
} from '@/store/actions/nav';
import {activateRefresh} from '@/store/actions/notifications';
import {
  containsInGroupHierarchy,
  createAncestorList,
  deletePreviousPage,
  getAllAccountIds,
  getFilterRequestParams,
  getIONPrimeURL,
  getPreviousPage
} from '@/store/helpers';
import {ApplicationKeys} from '@/types/application';
import '../../props/GlobalProps';
import {getDefaultPage} from '../../routes/RouteSecurity';
import {
  GLOBAL_GROUP_INITIALIZED,
  refreshAccountHierarchy,
  SET_CURRENT_ACCOUNT,
  SET_CURRENT_WEBMAPID,
  SET_GLOBAL_GROUP
} from './global-group-chooser';
import {resetFilterTreeNodes, setSCDeviceTypes} from './groups-filter';
import {toggleAccessError} from './helpers';
import {applyAssetHistoryDateFilter, retrieveReportOptions} from './reports';
import {launchSHR, setAssetDetailView, setSelectedAsset, setSideSheetView, setTrackingMode, viewAssetHistory} from './tracking';

export const PRE_INITIALIZATION_REQUEST          = 'PRE_INITIALIZATION_REQUEST';
export const PRE_INITIALIZATION_COMPLETE         = 'PRE_INITIALIZATION_COMPLETE';
export const PRE_INITIALIZATION_ERROR            = 'PRE_INITIALIZATION_ERROR';

export const APPLICATION_INITIALIZATION_REQUEST  = 'APPLICATION_INITIALIZATION_REQUEST';
export const APPLICATION_INITIALIZATION_COMPLETE = 'APPLICATION_INITIALIZATION_COMPLETE';
export const APPLICATION_INITIALIZATION_ERROR    = 'APPLICATION_INITIALIZATION_ERROR';

export const POST_INITIALIZATION_REQUEST         = 'POST_INITIALIZATION_REQUEST';
export const POST_INITIALIZATION_COMPLETE        = 'POST_INITIALIZATION_COMPLETE';
export const POST_INITIALIZATION_ERROR           = 'POST_INITIALIZATION_ERROR';

export const REFRESH_INITIALIZATION_REQUEST      = 'REFRESH_INITIALIZATION_REQUEST';
export const REFRESH_INITIALIZATION_COMPLETE     = 'REFRESH_INITIALIZATION_COMPLETE';
export const REFRESH_INITIALIZATION_ERROR        = 'REFRESH_INITIALIZATION_ERROR';

export const INSUFFICIENT_PERMISSIONS_ERROR      = 'INSUFFICIENT_PERMISSIONS_ERROR';
export const FEATURE_UNSUPPORTED_ERROR           = 'FEATURE_UNSUPPORTED_ERROR';

// Deep link actions
export const LOAD_DEEP_LINK_REQUEST  = 'LOAD_DEEP_LINK_REQUEST';
export const LOAD_DEEP_LINK_RESPONSE = 'LOAD_DEEP_LINK_RESPONSE';
export const LOAD_DEEP_LINK_ERROR    = 'LOAD_DEEP_LINK_ERROR';

// Variable name for localStorage
export const USER_NAME_VERIFICATION_KEY  = 'LOGIN_USERNAME';

// defines the initialize method params...
export interface InitializeParams {
  user: User,
  account: Account,
  route?: any
}

/**
 * Starts application initialization performance monitoring. This function initializes
 * and starts the performance monitor to track application initialization time metrics.
 *
 * The Process of Application Initialization Performance Monitoring is as follows:
 * 1. Retrieves the initialization performance monitor instance
 * 2. Checks if the monitor exists and is not already running
 * 3. Starts the monitor to begin collecting performance metrics
 *
 * This function is invoked at the beginning of the application initialization process
 * to measure performance metrics pertaining to application startup time.
 */
export const metricsBegin = () => {
  const monitor = getInitializationPerformanceMonitor();
  if (monitor && !monitor.isRunning()) {
    monitor.start();
  }
};
/**
 * Stops application initialization performance monitoring. This function stops the
 * performance monitor which was started by `metricsBegin` to complete the tracking
 * of application initialization performance metrics.
 *
 * The Process of completing Application Initialization Performance Monitoring is
 * as follows:
 *
 * 1. Retrieves the initialization performance monitor instance
 * 2. Checks if the monitor exists and is currently running
 * 3. Stops the monitor to finalize collecting performance metrics
 *
 * This function is invoked at the end of the application initialization process
 * (`initializationComplete`) to complete the measuring of performance metrics
 * pertaining to application startup time.
 */
export const metricsEnd = () => {
  const monitor = getInitializationPerformanceMonitor();
  if (monitor?.isRunning()) {
    monitor.stop();
  }
};

/**
 * @initializePreferences
 *
 * Initializes user preferences for the application. The function performs the following steps:
 *
 * 1. Initializes the UserPreferences service with the user's Id and extended attributes.
 * 2. Transfers (lazily) preferences for the iOnSuite application (if required).
 * 3. Persists default notification preferences via `user.account.id`.
 * 4. Persists Default Language Preferences via `account.language`.
 * 5. Retrieves the user's timezone preference or falls back to user settings.
 * 6. Sets the application timezone based on user preference (if applicable), followed by
 *    `user.timeZone` (if set), or defaults to `account.timeZone`.
 */
const initializePreferences = async(user: User, account: Account, value?: PreferencesStore) => {
  await UserPreferences.initialize(
    user.id,
    parseExtendedAttributes(user, true)
  );
  UserPreferences.transfer(ApplicationKeys.iOnSuite, {
    remove    : false,
    override  : false,
    immediate : false
  });
  UserPreferences.persistDefaultLanguage(account?.language);
  setUserTimeZone(
    UserPreferences.get(Preference.userTimeZone)?.timeZone || user.timeZone || account.timeZone
  );
  initializeAnalyticsStore();
};

/**
 * @preinitialization
 *
 * Provides the application pre-authentication initialization action. This
 * action manages all pre-initialization configurations required prior to
 * authentication.
 */
export const preinitialization = () => {
  info('Application Pre Initialization ...');
  return dispatch => {
    addErrorInterceptor(dispatch);
    info('Application Pre-initialization Complete');
    dispatch({type: PRE_INITIALIZATION_COMPLETE});
  };
};

/**
 * Provides a convenience which function verifies that the current user call returns
 * the same username as that which was used when authenticating with CTC.
 */
const verifyCurrentUser = (user: User, dispatch: Function) => {
  const verifiedUsername = getLocalStorage(USER_NAME_VERIFICATION_KEY);
  const currentUsername  = user.username.toLowerCase();
  const isUserEmpty = Object.keys(user).length === 0 || user?.username === '';
  const isValid = localStorage && (verifiedUsername === currentUsername);
  if (!isValid || isUserEmpty) {
    error(`ION SESSION ERROR: Invalid Current User Response ${currentUsername}. Verified user is: ${verifiedUsername}`);
    Pendo.track('ION SESSION ERROR', {
      severity : 'FATAL',
      currentUsername,
      verifiedUsername
    });
    deleteLocalStorage(USER_NAME_VERIFICATION_KEY);
    get('oidcEnabled') ? dispatch(authMgr.oidcLogout()) : dispatch(logout());
    return false;
  }
  return true;
};

/**
 * keepAlive function provides periodic request to CTC to keep the token alive.
 * This function is mainly used for `ADMIN_COMMAND_CENTER` feature.
 */
const keepAlive = (dispatch, interval = 60) => {
  const intervalId = setInterval(async() => {
    try {
      await authMgr.renewToken();
      const user = await currentUserQuery();
      if (verifyCurrentUser(user, dispatch)) {
        info('CTC Ping Request Issued Successfully');
      }
    } catch (err) {
      error('CTC Ping Request Failed', err);
      clearInterval(intervalId);
      addErrorInterceptor(dispatch);
    }
  }, interval * 60 * 1000);
};

/**
 * @initialize
 *
 * Provides the application post-authentication initialization action. This
 * action manages all post-authentication initialization requests required
 * by the application prior to rendering the first page. The function performs the following steps:
 *
 * 1. Dispatches initialization request action
 * 2. Persists default language from account settings
 * 3. Initializes user preferences and notification preferences in parallel
 * 4. Retrieves user roles and application key
 * 5. Initializes analytics tracking (Pendo) in production environments
 * 6. Fetches role and application data from initialization query
 * 7. Retrieves account hierarchy (subaccounts)
 * 8. Extracts application features from extended attributes
 * 9. Persists username verification in local storage
 * 10. Initializes Features service with user permissions
 * 11. Sets read-only mode if applicable
 * 12. Dispatches initializationComplete action which triggers:
 *     - Page initialization
 *     - Global group chooser initialization
 *     - Web map and filter initialization
 *     - Command center keepalive setup if applicable
 */
export const initialize = ({
  user,
  account,
  route
}: InitializeParams) => {
  return async dispatch => {
    info('Application Initialization ...');
    dispatch({type: APPLICATION_INITIALIZATION_REQUEST, route});
    initializePreferences(user, account);
    try {
      const {enriched} = user;
      const roles     = enriched.roles;
      const appKey    = get('appKey');
      const accountId = entityId(account);
      const userRole  = getRole(roles, appKey);
      const isDev     = isDevMode();
      if (!isDev) {
        Pendo.initialize(user, {application: ApplicationKeys.iOnSuite});
        if (Dynatrace.isEnabled()) {
          Dynatrace.identifyUser(`${user.id}`, {
            username    : user.username,
            accountName : user.account?.title
          });
        }
        if (Datadog.isEnabled()) {
          Datadog.identifyUser({
            userId      : user.id,
            username    : user.username,
            accountName : user.account?.title,
            accountId
          });
        }
      }
      const {role, application} = await initializationQuery(userRole.id);
      const accounts   = await getSubAccounts(accountId, true);
      const attributes = parseExtendedAttributes(application, true);
      const allRoles   = roles.map((item: Role) => (
        item.id === role.id ? role : item
      ));
      setLocalStorage(USER_NAME_VERIFICATION_KEY, user.username.toLowerCase());
      Features.initialize({
        enableLogging : isDev,
        allFeatures   : attributes.features,
        roles         : allRoles,
        resolved      : true,
        appKey,
        user
      });
      Features.updateRoleFeatures(FeatureFlags.getRoleFeatures());
      // the theme a user last chose was applied before sign-in - now signed
      // in, the current theme is kept only with the `dls` Feature Flag ...
      verifyThemeAccess();
      const isReadOnlyUser = Features.isReadOnlyUser();
      setReadOnlyMode(isReadOnlyUser);

      const isPermitted = verifyCrossEnvironmentAccess(Features) !== false;
      if (isPermitted) {
        dispatch(initializationComplete({accounts, role, user, account, isReadOnlyUser}, route));
      }
    } catch (err) {
      dispatch({type: APPLICATION_INITIALIZATION_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @initializationComplete
 *
 * Defines the initialization complete action which is invoked upon successful
 * initialization of all post-authentication service calls, after which, the
 * first page is ready to be rendered.
 *
 * Notes:
 * - getWebMapId() in SET_CURRENT_WEBMAPID relies on a group's inherited properties
 *   being loaded. For the user's assigned group, this was done above in initialize()
 *   via UserPreferences.persistDefaultNotificationsPreferences().
 * - SET_GLOBAL_GROUP does not need to be explicitly dispatched for the user's assigned
 *   group as the dispatch of APPLICATION_INITIALIZATION_COMPLETE results in the same changes
 *   in various reducers.
 * - SET_CURRENT_ACCOUNT does not need to be explicitly dispatched for the user's assigned
 *   group as the dispatch of LOGIN_SUCCEEDED previously set the account.
 */
export const initializationComplete = function(items, route = null) {
  return async dispatch => {
    const {account, user, role} = items;
    const defaultGroupId = UserPreferences.get(Preference.defaultGroupId);
    EntityLabel.initialize(role);
    dispatch({
      type             : APPLICATION_INITIALIZATION_COMPLETE,
      canUpdateGroupId : isNil(defaultGroupId) || (defaultGroupId === account.id),
      route,
      ...items
    });
    metricsEnd();
    UserPreferences.persistDefaultNotificationsPreferences(entityId(user.account));
    await dispatch(initializeGlobalGroupChooser(user, account, route));
    dispatch(initializePage(route));
    dispatch(resetFilterTreeNodes({nodes: null}));
    dispatch(setSCDeviceTypes());

    if (Features.hasFeature(ADMIN_COMMAND_CENTER) && get('oidcEnabled')) {
      keepAlive(dispatch);
    }
  };
};

/**
 * @initializeGlobalGroupChooser
 *
 * Initializes the global group chooser based on user preferences and account hierarchy.
 * The function performs the following steps:
 *
 * 1. Retrieves the default group ID from user preferences
 * 2. Checks if a different group than the user's primary account is selected
 * 3. Resolves the group from the account map or falls back to first available group
 * 4. Creates ancestor account list for hierarchical navigation
 * 5. Updates the current account in Features service
 * 6. Dispatches actions to update global group state
 * 7. Updates Sisense analytics settings for the selected group
 * 8. Marks global group initialization as complete
 */
const initializeGlobalGroupChooser = (user: User, account: Account, route = null) => {
  return async(dispatch, getState) => {
    let defaultGroupId = UserPreferences.get(Preference.defaultGroupId);
    await GroupProperties.getInheritedProperties(defaultGroupId || entityId(account));
    if (defaultGroupId && (defaultGroupId !== account.id)) {
      const {accountMap} = getState().globalGroup;
      let defaultGroup   = accountMap[defaultGroupId];
      if (!defaultGroup) {
        defaultGroupId = parseInt(Object.keys(accountMap)?.[0]);
        defaultGroup   = accountMap[defaultGroupId];
      }
      const ancestorAccountId = createAncestorList(defaultGroupId);
      Features.setCurrentAccount(defaultGroup);
      const account = await getAccount(defaultGroupId);
      await Promise.all([
        dispatch({type: SET_GLOBAL_GROUP, group: defaultGroup, ancestorAccountId, route, user}),
        dispatch({type: SET_CURRENT_ACCOUNT, account})
      ]);

      Sisense.setAccountId(defaultGroupId);
      Sisense.updateSettings({
        ...constructDriverScores(getScoresParams({account} as any))
      });
    }
    dispatch({type: GLOBAL_GROUP_INITIALIZED});
    dispatch({type: SET_CURRENT_WEBMAPID, webMapId: getWebMapId()});
  };
};

/**
 * @initializePage
 *
 * Provides a dedicated internal action for initializing the current page based on
 * user permissions and routing context. The function performs the following steps:
 *
 * 1. Determines the default route from navigation state
 * 2. Checks if user has permission for the default route
 * 3. Handles OIDC authentication state and stored previous page
 * 4. Resolves current page based on permissions and login state
 * 5. Processes query parameters for deep linking if present
 * 6. Redirects to appropriate page based on permissions
 * 7. Dispatches post-initialization actions or logs out if insufficient permissions
 */
const initializePage = (route = null) => {
  return async(dispatch, getState) => {
    const state            = getState();
    const defaultRoute     = getRoute(state.nav.defaultPage);
    const isAllowedFeature = Features.hasFeature(getFeatureByRoute(getRouteDefinition(defaultRoute)));
    const hasiOnSuite      = state.login.isSuiteOnly || getState().login.hasPreviewFeature;
    const currentPageState = state.nav.currentPage ?? route?.pathname;

    let defaultPage    = getDefaultPage(defaultRoute);
    const currentPage  = isAllowedFeature ? getRoute(currentPageState) : defaultPage;
    const oidcEnabled  = get('oidcEnabled');
    let previousPage   = getRoute(getState().nav.previousPage);
    let storedPrevPage = null;
    if (oidcEnabled) {
      storedPrevPage = {...getPreviousPage()};
      previousPage = storedPrevPage?.route;
      dispatch(setQueryParams({pathname: previousPage, queryParams: storedPrevPage?.query}));
      deletePreviousPage();
    }
    if (currentPage === getRoute(Route.LOGIN)) {
      defaultPage = getRoute(Route.TRACKING2);
    }
    // If there was a previous page when the app initializes, like redirection to login
    // then the user should be directed to that page over their default page.
    if (previousPage && getRouteDefinition(previousPage)) {
      defaultPage = previousPage;
    }
    const queryParams = oidcEnabled ? storedPrevPage?.query : state?.query?.queryParams;
    await dispatch(initializePageSecurity());
    if (hasiOnSuite) {
      if (queryParams) {
        dispatch(handleQueryParams());
      } else if (!isAllowedFeature) {
        dispatch(toggleAccessError(true, 'nav:pageAccessError'));
      }
      if (queryParams?.report === 'assetHistory') {
        defaultPage = '/tracking2';
      }
      dispatch(setPage(defaultPage));
      const includeQueryParams = (
        defaultPage === getRoute(Route.TRACKING) ||
        defaultPage === getRoute(Route.TRACKING2) ||
        defaultPage === getRoute(Route.REPORTS) ||
        APM.isEnabled
      );
      changePage(defaultPage as Route, undefined, includeQueryParams);
      dispatch(postInitialize());
    } else {
      if (oidcEnabled) {
        await authMgr.oidcLogout();
      } else {
        try {
          await logout();
        } catch (err) {
          error(`An exception occurred attempting to logout a User with insufficient iOn Permissions. ${err?.toString()}`);
        }
      }
      dispatch({type: INSUFFICIENT_PERMISSIONS_ERROR});
    }
  };
};

/**
 * @postInitialize
 *
 * Defines the post-initialization action which is invoked upon successful
 * initialization of all initialization service calls. The post initialization
 * process comprises non-blocking requests which are made in the background
 * subsequent to the first page being rendered and the application being ready
 * for use by the user.
 */
export const postInitialize = () => {
  return async(dispatch, getState) => {
    info('Application Post Initialization ...');
    dispatch({type: POST_INITIALIZATION_REQUEST});
    try {
      dispatch(retrieveReportOptions());
      let notifications = [];
      if (hasAppNotificationAccess() && !Features.isCurrentAccountRestricted()) {
        notifications = await getAlertsFired({accountIds: getAllAccountIds(getState)});
      }
      info('Application Post Initialization Complete');
      dispatch({type: POST_INITIALIZATION_COMPLETE, notifications});
      if (hasAppNotificationAccess()) {
        dispatch(activateRefresh());
      }
    } catch (err) {
      error(err);
      dispatch({type: POST_INITIALIZATION_ERROR});
    }
  };
};

/**
 * @refreshInitialization
 *
 * Invoked any time after initialization has completed (pre, post, etc.) when a specific
 * piece of initialization state needs to be refreshed (such as when an account is added
 * to the users account hierarchy, etc.).
 */
export const refreshInitialization = () => {
  return async(dispatch, getState) => {
    const login = getState().login;
    info('Application Initialization Refresh ...');
    dispatch({type: REFRESH_INITIALIZATION_REQUEST});
    try {
      const accounts = await getSubAccounts(entityId(login?.user.account), true);
      dispatch({type: REFRESH_INITIALIZATION_COMPLETE, accounts});
      dispatch(refreshAccountHierarchy(accounts));
    } catch (err) {
      dispatch({type: APPLICATION_INITIALIZATION_ERROR, error: err});
      error(err);
    }
  };
};

// defines all predefined valid CTAs into the app
// follows the syntax of query: value that would be passed in as ?query=value in the URL
const validCTAs = [
  {report: 'assetHistory'},
  {report: 'shipmentHistory'},
  {detail: 'assetDetail'},
  {detail: 'deviceDetail'}
];

/**
 * @isValidCTA
 *
 * Checks the query params passed in against a list of predefined CTAs.
 */
const isValidCTA = (queryParams) => {
  let isValid = false;
  for (const cta of validCTAs) {
    if (some([queryParams], cta)) {
      isValid = true;
      break;
    }
  }
  return isValid;
};

/**
 * Action creator that can be used for firing off function based
 * on any query params. Should typically be done in initialization complete
 * so that query params have been captured and only done when application
 * loads. This way we don't need to continuously clear the query when the user
 * navigates around the app.
 */
export const handleQueryParams = () => {
  return async(dispatch: Function, getState: Function) => {
    const {
      query : {
        queryParams
      }
    } = getState();
    const defaultRoute     = getRoute(getState().nav.defaultPage);
    const isAllowedFeature = Features.hasFeature(getFeatureByRoute(getRouteDefinition(defaultRoute)));
    if (isAllowedFeature) {
      if (isValidCTA(queryParams)) {
        if (queryParams?.report === 'assetHistory') {
          dispatch(deepLinkAssetHistory());
        } else if (queryParams?.report === 'shipmentHistory') {
          dispatch(deepLinkShipmentHistory());
        } else if (queryParams?.assetDetail) {
          dispatch(deepLinkAssetDetail());
        } else if (queryParams?.deviceDetail) {
          dispatch(deepLinkDeviceDetail());
        }
      }
      /* ADD ADDITIONAL QUERY HANDLING HERE */
    } else {
      dispatch(toggleAccessError(true, 'nav:pageAccessError'));
    }
  };
};

/**
 * Query params should have the deviceId and date info to be able to
 * launch the shipment history report for the device on the passed date.
 */
const deepLinkShipmentHistory = () => {
  return async(dispatch: Function, getState: Function) => {
    const {
      query : {
        queryParams
      }
    } = getState();
    const {
      deviceId,
      date
    } = queryParams || {};
    if (deviceId) {
      try {
        const devices = await getDevice(Number(deviceId));
        const dateNum = Number(date);
        const dateVal = !isNaN(dateNum) ? dateNum : date;
        const {
          account: {
            href: accountHref,
            title: accountName
          },
          asset,
          id,
          name
        } = devices?.[0] || {account: {}, asset: {}};
        if (containsInGroupHierarchy(entityId(accountHref), getState)) {
          const {
            href: assetHref,
            title: assetName
          } = asset || {};
          const deviceCollection: any = {
            accountId  : entityId(accountHref),
            assetId    : assetHref ? entityId(assetHref) : null,
            deviceId   : id,
            deviceName : name,
            isScDevice : true,
            accountName,
            assetName,
            id
          };
          dispatch(launchSHR({
            selectedDevice : deviceCollection,
            redirectPage   : false,
            date           : dateVal
          }) as any);
        } else {
          dispatch(setDeepLinkRestriction(true));
          dispatch(launchSHR({
            selectedDevice : null,
            redirectPage   : false,
            date           : dateVal
          }) as any);
          dispatch(toggleAccessError());
        }
      } catch (err) {
        error(err);
      }
    }
  };
};

/**
 * Query params should have the assetId and date info to be able to
 * launch the asset history report for the asset on the passed date.
 */
const deepLinkAssetHistory = () => {
  return async(dispatch: Function, getState: Function) => {
    const {
      query : {
        queryParams
      }
    } = getState();
    if (queryParams?.assetId) {
      const {
        assetId,
        date
      } = queryParams;
      try {
        // fetch asset from CVS
        const asset     = await getAsset(Number(assetId));
        const {account} = asset || {};
        const dateNum   = Number(date);
        const dateVal   = !isNaN(dateNum) ? dateNum : date;
        // in the current theme the report a deep link (a notification's View
        // Event) opens is a History report like any other, so the Time Mode
        // control is moved to History over the same day the report runs over ...
        const historySelection = {
          preset : dateVal ? DateSelectorValues.Custom : DateSelectorValues.Today,
          from   : startOfDay(dateVal),
          to     : endOfDay(dateVal)
        };
        if (containsInGroupHierarchy(entityId(account?.href), getState)) {
          const assetAsDevice = !isObjectEmpty(asset)
            ? Device.getAssetsFromCollection([asset])[0]
            : null;
          dispatch(viewAssetHistory({selectedAsset: assetAsDevice, dateVal}));
          dispatch(setTrackingMode(TrackingMode.History));
          applyHistoryTimeMode(dispatch, historySelection);
        } else {
          dispatch(setDeepLinkRestriction(true));
          dispatch(applyAssetHistoryDateFilter({
            fromDate      : startOfDay(dateVal),
            toDate        : endOfDay(dateVal),
            selectedRadio : dateVal ? DateSelectorValues.Custom : DateSelectorValues.Today
          }));
          dispatch(setTrackingMode(TrackingMode.History));
          applyHistoryTimeMode(dispatch, historySelection);
          dispatch(toggleAccessError());
        }
      } catch (err) {
        error(err);
      }
    }
  };
};

export const deepLinkAssetDetail = () => {
  return async(dispatch: Function, getState: Function) => {
    const {
      query : {
        queryParams : {
          assetDetail
        }
      },
      tracking : {
        filters: {
          assetTypes,
          assetCategories,
          geoFences,
          routes,
          assetStatus
        }
      }
    } = getState();
    dispatch(setTrackingMode(TrackingMode.Live));
    const assetId   = parseInt(assetDetail);
    const asset     = await getAsset(Number(assetId));
    const {account} = asset || {};
    if (containsInGroupHierarchy(entityId(account?.href), getState)) {
      const getCVSAsset = async() => {
        const params = {
          assetId         : parseInt(assetId),
          assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
          assetCategories : [...getFilterRequestParams(assetCategories.selected)],
          geoFences       : [...getFilterRequestParams(geoFences.selected)],
          routes          : [...getFilterRequestParams(routes.selected)],
          assetStatus     : [...getFilterRequestParams(assetStatus.selected)]
        } as CVSQueryArgs;
        return await getCVSAssets(params);
      };
      const assets        = await getCVSAsset();
      const selectedAsset = assets?.[0] || assets;
      dispatch(setSelectedAsset({asset: selectedAsset}));
      dispatch(setSideSheetView(SideSheetView.detailed));
      dispatch(setAssetDetailView({
        value            : true,
        forcePanelToOpen : false,
        fromTools        : false,
        selectedItem     : selectedAsset
      }));
      dispatch(setSideSheetStatus({
        activeSideSheet      : `/${Route.TRACKING2}`,
        desktopSideSheetOpen : true,
        mobileSideSheetOpen  : false
      }));
    } else {
      dispatch(toggleAccessError(true));
    }
  };
};

export const deepLinkDeviceDetail = () => {
  return async(dispatch: Function, getState: Function) => {
    const {
      query : {
        queryParams : {
          deviceDetail
        }
      }
    } = getState();
    const deviceId = parseInt(deviceDetail);
    const devices  = await getDevice(Number(deviceId));
    const {
      account: {
        href: accountHref
      }
    } = devices?.[0] || {account: {}, asset: {}};
    if (!containsInGroupHierarchy(entityId(accountHref), getState)) {
      dispatch(setTrackingMode(TrackingMode.Live));
      dispatch(toggleAccessError(true));
    } else {
      dispatch(setSideSheetStatus({
        activeSideSheet      : 'tracking2',
        desktopSideSheetOpen : true,
        mobileSideSheetOpen  : false
      }));
    }
  };
};
